import useCheckoutOfferData from 'checkout/hooks/useCheckoutOfferData'
import { useRestoreAbandonedCartState } from 'checkout/hooks/useRestoreAbandonedCartState'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import {
  AcSupportedAccommodationOfferView,
} from 'components/Recommendations/StickyCard/StickyAdandonedCart/StickyAbandonedCartCard'
import config from 'constants/config'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { sum } from 'lib/array/arrayUtils'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { isTourPurchasable } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { isOfferAvailable } from 'selectors/recommendations'
import { useParams } from 'react-router'
import { isHeadlessModeSel } from 'selectors/configSelectors'
import { getLatestAbandonedCart } from 'selectors/recomendations/highIntentSelectors'
import useCheckoutTourV2Data from 'checkout/hooks/useCheckoutTourV2Data'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { useIsMobileScreen } from 'lib/web/deviceUtils'

const isHotel = (item: App.Checkout.AccommodationOfferView): item is App.Checkout.LEAccommodationOfferView | App.Checkout.BedbankAccommodationOfferView => {
  return (item.offerType === OFFER_TYPE_ALWAYS_ON ||
    item.offerType === OFFER_TYPE_LAST_MINUTE ||
    item.offerType === OFFER_TYPE_BED_BANK ||
    item.offerType === OFFER_TYPE_HOTEL)
}

function isTourV2(item: App.Checkout.AccommodationOfferView): item is App.Checkout.TourV2AccommodationOfferView {
  // v1 tour is no longer in prod
  return item.offerType === OFFER_TYPE_TOUR_V2
}

export function isCruiseV1(item: App.Checkout.AccommodationOfferView): item is App.Checkout.LEAccommodationOfferView {
  // cruise v1 is implemented as tour v1 (whereas the real tour v1 is no longer in prod)
  return item.offerType === OFFER_TYPE_TOUR
}

function isHotelAvailable(hotelView: App.Checkout.LEAccommodationOfferView | App.Checkout.BedbankAccommodationOfferView) {
  return (hotelView.offerType === OFFER_TYPE_BED_BANK || (!!hotelView.offer && isOfferAvailable(hotelView.offer))) && getTotalPrice(hotelView) > 0
}

function isTourV2Available(tourView: App.Checkout.TourV2AccommodationOfferView) {
  return !!tourView.offer && isTourPurchasable(tourView.offer) && getTotalPrice(tourView) > 0
}

function isCruiseV1Available(cruiseView: App.Checkout.LEAccommodationOfferView) {
  return !!cruiseView.offer && isOfferAvailable(cruiseView.offer) && getTotalPrice(cruiseView) > 0
}

export function isAbandonedCartSupportedAndAvailable(item: App.Checkout.AccommodationOfferView): item is AcSupportedAccommodationOfferView {
  return (
    (isHotel(item) && isHotelAvailable(item)) ||
    (isTourV2(item) && isTourV2Available(item)) ||
    (isCruiseV1(item) && isCruiseV1Available(item))
  )
}

export function getTotalPrice(offerView: AcSupportedAccommodationOfferView) {
  const price = sum<App.Checkout.ItemTotals>(offerView.itemViews, (item) => (item.price ?? 0) + (item.surcharge ?? 0))
  return price
}

export default function useAbandonedCartSelectionLogic({ abandonedCartCardEnabled }: {abandonedCartCardEnabled: boolean}): {
  isCartDataReady: boolean,
  canShowAbandonedCartCard: boolean,
  abandonedCartOfferView?: AcSupportedAccommodationOfferView,
} {
  const {
    userId,
    currentRegion,
    isHeadless,
  } = useAppSelector((state) => ({
    userId: getCurrentUserId(state),
    currentRegion: state.geo.currentRegionCode,
    isHeadless: isHeadlessModeSel(state),
  }), shallowEqual)

  const isMobile = useIsMobileScreen()

  const canUseAbandonedCart = (
    !isHeadless &&
    abandonedCartCardEnabled &&
    config.LERE_STICKY_ABANDONED_CARTS_ENABLED &&
    isEnabledForFeature(config.LERE_STICKY_ABANDONED_CARTS_ENABLED_COUNTRIES, currentRegion) &&
    !!userId &&
    isMobile
  )

  // Get abandoned cart data
  useRestoreAbandonedCartState(canUseAbandonedCart)
  useCheckoutOfferData()
  useCheckoutTourV2Data()

  // the offerId from the URL, which comes from the offer page
  // so if the ID is the same as AC offer ID, we don't show the AC card
  const { offerId: offerPageOfferId } = useParams<{offerId: string}>()

  const {
    abandonedCart,
    accommodationView,
  } = useAppSelector((state) => {
    const abandonedCart = getLatestAbandonedCart(state)
    return {
      abandonedCart,
      accommodationView: (canUseAbandonedCart && !!abandonedCart) ? checkoutAccommodationOfferView(state) : undefined,
    }
  }, shallowEqual)

  const abandonedCartOfferView = accommodationView?.data?.find(isAbandonedCartSupportedAndAvailable)
  const offer = abandonedCartOfferView?.offer
  const isFlightBundled = isFlightForceBundled(offer) || !!abandonedCartOfferView?.isBundled
  const isViewingOfferPage = !!offer && (offer.id === offerPageOfferId)

  const canShowAbandonedCartCard = (
    canUseAbandonedCart &&
    !!abandonedCart &&
    !abandonedCart.isDismissed &&
    !!abandonedCartOfferView &&
    !isFlightBundled &&
    !isViewingOfferPage
  )

  return {
    isCartDataReady: !canUseAbandonedCart || !abandonedCart || isFlightBundled || !!accommodationView?.hasRequiredData,
    canShowAbandonedCartCard,
    abandonedCartOfferView,
  }
}
